import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Alert, Button, Card, Col, Descriptions, Form, Input, InputNumber, Modal, Row, Space, Switch, Table, Tag, Typography } from "antd"
import { useNavigate } from "react-router-dom"
import { Contract, DeliverySelectionStatus, Selection, SelectionPolicy, SelectionStats } from "../store/types"

const SELECTION_COLOR: Record<DeliverySelectionStatus, string> = {
  PENDING: "default",
  PLANNING: "processing",
  DRAFT: "blue",
  REVIEWING: "gold",
  APPROVED: "cyan",
  EXECUTED: "green",
  DISCARDED: "default",
  FAILED: "red",
}

// Statuses where the async planner is still working, so the list should poll.
const IN_FLIGHT: DeliverySelectionStatus[] = ["PENDING", "PLANNING"]

const StatsSummary: FC<{ stats: SelectionStats }> = ({ stats }) => (
  <Space direction="vertical" style={{ width: "100%" }} size={8}>
    <Descriptions column={4} size="small" bordered>
      <Descriptions.Item label="Player-hours">
        {stats.selectedHours.toFixed(1)} h <Typography.Text type="secondary">of {stats.targetHours} h target</Typography.Text>
      </Descriptions.Item>
      <Descriptions.Item label="Sessions">{stats.sessionCount}</Descriptions.Item>
      <Descriptions.Item label="Recordings">{stats.recordingCount}</Descriptions.Item>
      <Descriptions.Item label="Distinct games">{stats.gameCount}</Descriptions.Item>
      <Descriptions.Item label="2 players">{stats.squadHours.twoPlayer.toFixed(1)} h</Descriptions.Item>
      <Descriptions.Item label="3 players">{stats.squadHours.threePlayer.toFixed(1)} h</Descriptions.Item>
      <Descriptions.Item label="4+ players">{stats.squadHours.fourPlusPlayer.toFixed(1)} h</Descriptions.Item>
      <Descriptions.Item label="By tier">
        {Object.entries(stats.tierHours).map(([tier, hours]) => (
          <Tag key={tier}>{tier}: {hours.toFixed(1)} h</Tag>
        ))}
      </Descriptions.Item>
    </Descriptions>

    {!!stats.capOverrunGameIds?.length && (
      <Alert
        type="warning"
        showIcon
        message="A pre-included session pushed a game past its remaining contract cap"
        description={
          `Games: ${stats.capOverrunGameIds.join(", ")}. Seeded sessions are admitted even over cap because a human vetted `
          + "them — the overrun is reported, not silently dropped."
        }
      />
    )}

    {/* A thin selection is almost always an exclusion problem, so show why
        candidates were dropped rather than leaving the operator guessing. */}
    <Card size="small" title="Why candidates were dropped">
      <Descriptions column={4} size="small">
        <Descriptions.Item label="Eligible sessions scanned">{stats.excluded.eligibleSessions}</Descriptions.Item>
        <Descriptions.Item label="Member not reservable">{stats.excluded.notReservable}</Descriptions.Item>
        <Descriptions.Item label="Operator-excluded">{stats.excluded.operatorExcluded}</Descriptions.Item>
        <Descriptions.Item label="Recovery child">{stats.excluded.recovered}</Descriptions.Item>
        <Descriptions.Item label="Member not validatable">{stats.excluded.eligibilityUnknown}</Descriptions.Item>
        <Descriptions.Item label="Gamepad / lobby">{stats.excluded.gamepadOrLobby}</Descriptions.Item>
        <Descriptions.Item label="Incomplete closure">{stats.excluded.closureIncomplete ?? 0}</Descriptions.Item>
        <Descriptions.Item label="No members">{stats.excluded.noMembers}</Descriptions.Item>
      </Descriptions>
    </Card>

    <Card size="small" title={`Per-game · ${stats.byGame.length}`}>
      <Table
        rowKey="gameId"
        size="small"
        dataSource={stats.byGame}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        columns={[
          { title: "Game", key: "game", render: (_: unknown, g) => `${g.gameId} · ${g.gameName ?? "—"}` },
          { title: "Tier", dataIndex: "tier", key: "tier" },
          { title: "Sessions", dataIndex: "sessions", key: "sessions" },
          { title: "Hours", key: "hours", render: (_: unknown, g) => `${g.playerHours.toFixed(1)} h` },
          { title: "Contract cap", key: "cap", render: (_: unknown, g) => `${g.contractCap} h` },
          { title: "Already delivered", key: "delivered", render: (_: unknown, g) => `${g.deliveredHours.toFixed(1)} h` },
          { title: "Room left", key: "remaining", render: (_: unknown, g) => `${g.remainingHours.toFixed(1)} h` },
        ]}
      />
    </Card>
  </Space>
)

export const SelectionPlanner: FC<{ contract: Contract }> = observer(({ contract }) => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    store.loadSelections(contract.id)
  }, [store, contract.id])

  // Planning happens off the request path, so poll while any selection for this
  // contract is still being computed.
  const planning = store.selections.some(sel => IN_FLIGHT.includes(sel.status))
  useEffect(() => {
    if (!planning) return undefined
    const t = setInterval(() => store.loadSelections(contract.id), 3000)

    return () => clearInterval(t)
  }, [store, contract.id, planning])

  const runPlan = async() => {
    let v
    try {
      v = await form.validateFields()
    } catch {
      return
    }
    // Only send fields the operator actually filled: anything omitted falls
    // back to the contract policy, then to the service default.
    //
    // excludeRecovered is sent as a real boolean, NOT `|| undefined` — sending
    // undefined when the box is unchecked would fall back to a contract policy
    // that says true, so the operator could never turn it off.
    const policy: SelectionPolicy = {
      targetHours: v.targetHours ?? undefined,
      perGameBatchCapHours: v.perGameBatchCapHours ?? undefined,
      maxPlayers: v.maxPlayers ?? undefined,
      excludeRecovered: v.excludeRecovered === undefined ? undefined : !!v.excludeRecovered,
      seedSessionIds: splitIds(v.seedSessionIds),
      excludeRecordingIds: splitIds(v.excludeRecordingIds),
    }
    setSubmitting(true)
    const res = await store.planSelection({
      clientId: contract.clientId,
      contractId: contract.id,
      name: v.name || undefined,
      policy: stripEmpty(policy),
    })
    setSubmitting(false)
    if (res?.selection) {
      setOpen(false)
      form.resetFields()
      navigate(`/data-delivery/selections/${res.selection.id}`)
    }
  }

  const columns = [
    {
      title: "Selection",
      dataIndex: "id",
      key: "id",
      render: (id: string) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/data-delivery/selections/${id}`)}>
          {id.slice(0, 8)}…
        </Button>
      ),
    },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: DeliverySelectionStatus) => <Tag color={SELECTION_COLOR[s]}>{s}</Tag>,
    },
    { title: "Sessions", dataIndex: "sessionCount", key: "sessionCount" },
    { title: "Recordings", dataIndex: "recordingCount", key: "recordingCount" },
    { title: "Hours", key: "hours", render: (_: unknown, s: Selection) => `${s.playerHours.toFixed(1)} h` },
    {
      title: "Review sample",
      key: "sample",
      render: (_: unknown, s: Selection) => (s.sampleSize ? `${s.sampleSize} sessions` : "—"),
    },
    {
      title: "Batches",
      key: "batches",
      // batchCount, not batchIds.length: the id list is a capped preview.
      render: (_: unknown, s: Selection) => (s.batchCount ? s.batchCount : "—"),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Card
        size="small"
        title="Co-play selections"
        extra={<Button type="primary" size="small" onClick={() => setOpen(true)}>Plan selection</Button>}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          A selection proposes what a batch should contain under this contract&apos;s terms. Nothing is reserved until you
          execute it, so a plan can be re-run, sampled and discarded freely. Planning runs in the background — the row
          appears immediately and fills in when it completes.
        </Typography.Paragraph>
        <Table rowKey="id" size="small" columns={columns} dataSource={store.selections} loading={store.isLoading} />
      </Card>

      <Modal
        title="Plan a co-play selection"
        open={open}
        width={720}
        onCancel={() => setOpen(false)}
        okText="Create selection"
        confirmLoading={submitting}
        onOk={runPlan}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Blank fields use the contract&apos;s stored terms. Anything set here applies to this run only and is recorded on the
          selection, so the run stays reproducible.
        </Typography.Paragraph>
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="name" label="Name">
            <Input placeholder={`Batch ${new Date().toISOString().slice(0, 10)}`} />
          </Form.Item>
          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item name="targetHours" label="Target hours">
                <InputNumber min={0} style={{ width: "100%" }} placeholder="contract default" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="perGameBatchCapHours" label="Per-game cap this batch (h)">
                <InputNumber min={0} style={{ width: "100%" }} placeholder="contract default" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxPlayers" label="Max players">
                <InputNumber min={2} style={{ width: "100%" }} placeholder="contract default" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="excludeRecovered"
                label="Exclude recovered"
                valuePropName="checked"
                extra="Drop sessions containing a recovery child."
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="seedSessionIds"
            label="Pre-include sessions"
            extra="Operator-verified session ids, admitted first with their closure. Comma or newline separated."
          >
            <Input.TextArea rows={2} placeholder="uuid, uuid…" />
          </Form.Item>
          <Form.Item
            name="excludeRecordingIds"
            label="Exclude recordings"
            extra="e.g. recordings with an in-flight re-run. Comma or newline separated."
          >
            <Input.TextArea rows={2} placeholder="uuid, uuid…" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
})

export { StatsSummary }

function splitIds(raw?: string): string[] | undefined {
  const ids = (raw ?? "").split(/[\s,]+/).map(s => s.trim()).filter(Boolean)

  return ids.length ? ids : undefined
}

function stripEmpty(policy: SelectionPolicy): SelectionPolicy {
  return Object.fromEntries(Object.entries(policy).filter(([, v]) => v !== undefined)) as SelectionPolicy
}
